import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, betaUpdateGmailLabel } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { LabelModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface EditGmailLabelProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userLabels: LabelModel[];
  selectedLabel: LabelModel;
  changeLogRoles: string[];
}

export default function EditGmailLabelDialog({
  open,
  handleClose,
  userEmail,
  userLabels,
  selectedLabel,
  changeLogRoles,
}: EditGmailLabelProps) {
  const { createEvent } = useAnalytics();
  const [labelName, setLabelName] = useState(selectedLabel.name);
  const [labelVisibility, setlabelVisibilty] = useState(
    selectedLabel.labelVisibility
  );
  const [messageVisibility, setMessageVisibility] = useState(
    selectedLabel.messageVisibility
  );
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      betaUpdateGmailLabel(userEmail, selectedLabel, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Label'),
      });

      const newList = userLabels.filter((label) => label.name != data.name);
      newList.push(data);

      queryClient.setQueryData([UserKeys.GmailLabels, userEmail], newList);

      setHasError(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('the selected label'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .update_gmail_label,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.labels,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    const otherLabelNames = userLabels
      .filter((label) => label.id !== selectedLabel.id)
      .map((label) => label.name.toLocaleLowerCase());

    if (
      labelName.trim() !== '' &&
      otherLabelNames.includes(labelName.toLocaleLowerCase())
    ) {
      setHasError(true);
    } else {
      setHasError(false);
      selectedLabel.name = labelName;
      selectedLabel.labelVisibility = labelVisibility;
      selectedLabel.messageVisibility = messageVisibility;
      if (
        isAbleToSeeUIElement(changeLogRoles, UserRoles.EditGmailLabel.value)
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  const labelVisibiltyOptions = [
    { value: 'labelShow', displayValue: 'Show' },
    { value: 'labelHide', displayValue: 'Hide' },
    { value: 'labelShowIfUnread', displayValue: 'Show Label if Unread' },
  ];
  const messageVisibiltyOptions = [
    { value: 'show', displayValue: 'Show' },
    { value: 'hide', displayValue: 'Hide' },
  ];

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Edit Gmail Label"
      dialogContent={
        <Stack gap={2}>
          <TextField
            autoFocus
            id="labelName"
            error={hasError}
            helperText={hasError ? 'Label name is taken.' : ''}
            label="Label Name"
            fullWidth
            value={labelName}
            onChange={(event) => setLabelName(event.target.value)}
          />
          <Stack flexDirection="row" width="100%" gap={2}>
            <TextField
              label="Label Visibility"
              select
              fullWidth
              value={labelVisibility}
              onChange={(event) =>
                setlabelVisibilty(
                  event.target.value as LabelModel['labelVisibility']
                )
              }
            >
              {labelVisibiltyOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Message Visibility"
              value={messageVisibility}
              onChange={(event) =>
                setMessageVisibility(
                  event.target.value as LabelModel['messageVisibility']
                )
              }
            >
              {messageVisibiltyOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: labelName === '' || !messageVisibility || !labelVisibility,
        title: 'Save Changes',
      }}
    />
  );
}
